// src/app/pages/cost-metrics/cost-saving/cost-savings.component.ts
// Updates:
// - Use costMetricsAllYears (saved in simulations_list) to load Diagnosed, Treated, Controlled, CVD patients, CVD events
//   based on the mapped year per range: three->2028, five->2030, ten->2035, fifteen->2040.
// - Keep existing percentage formulas and base/current comparison using the selected latest simulation design.
// - Fallback to existing rows-based logic if all-years series are not found (or missing a metric/year).
// - Costs logic (primary/secondary) remains unchanged.

import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { AnalysisStore } from '../../../core/shared/analysis.store';

type RangeKey = 'three' | 'five' | 'ten' | 'fifteen';
type Percent = number;

interface KpiCard {
  label: string;
  icon: string;
  subtitle: string;
  percent: Percent;
  count: number;
}
type KpiSeriesKey = 'Diagnosed' | 'Treated' | 'Controlled' | 'CVD_Patients' | 'CVD_Events';

interface CareCost { base: number; current: number; diff: number; }

type CmSeries = {
  discountRate: number;
  discountSuffix: RangeKey;
  years: string[];
  series: Record<string, Record<string, number>>; // e.g., series['Diagnosed']['2040']
};

@Component({
  selector: 'app-cost-savings',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cost-savings.component.html'
})
export class CostSavingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() baseCm: any;
  @Input() currentCm: any;
  @Input() range: RangeKey = 'three';
  @Input() yearKey: string | number | undefined;
  // Selected (non-baseline) simulation id from parent
  @Input() currentRunId: string | null = null;

  // Legacy inputs (unused, kept for compatibility)
  @Input() diagnosedCount = 0;
  @Input() diagnosedPercent: Percent = 0;
  @Input() treatedCount = 0;
  @Input() treatedPercent: Percent = 0;
  @Input() controlledCount = 0;
  @Input() controlledPercent: Percent = 0;
  @Input() cvdPatientsCount = 0;
  @Input() cvdPatientsPercent: Percent = 0;
  @Input() cvdEventsCount = 0;
  @Input() cvdEventsPercent: Percent = 0;

  kpiCards: KpiCard[] = [];
  primaryCost: CareCost = { base: 0, current: 0, diff: 0 };
  secondaryCost: CareCost = { base: 0, current: 0, diff: 0 };

  private analysisStore = inject(AnalysisStore, { optional: true });
  private runsSub?: Subscription;
  private runsSnapshot: any[] = [];

  private debug = false;

  ngOnInit(): void {
    if (this.analysisStore) {
      this.runsSub = this.analysisStore.runsList$.subscribe((runs: any[]) => {
        this.runsSnapshot = Array.isArray(runs) ? runs : [];
        this.computeKpisFromRuns();
      });
    }
  }

  ngOnChanges(_: SimpleChanges): void {
    this.computeKpisFromRuns();
    this.computeCosts();
  }

  ngOnDestroy(): void {
    this.runsSub?.unsubscribe();
  }

  // Map range -> year (as requested)
  private getYearForRange(r: RangeKey): number {
    switch (r) {
      case 'three': return 2028;
      case 'five': return 2030;
      case 'ten': return 2035;
      case 'fifteen': return 2040;
      default: return 2045;
    }
  }

  private getResolvedYear(): string {
    const derived = this.getYearForRange(this.range);
    return String(this.yearKey ?? derived ?? '2045');
  }

  // ------------ KPI computation ------------
  private computeKpisFromRuns(): void {
    const yr = String(this.getYearForRange(this.range));

    if (!this.runsSnapshot?.length) {
      this.kpiCards = this.zeroCards();
      return;
    }

    const baseRun = this.pickBaseRun(this.runsSnapshot);
    const currentRun = this.pickCurrentRun(this.runsSnapshot, this.currentRunId);

    if (!baseRun || !currentRun) {
      this.kpiCards = this.zeroCards();
      return;
    }

    // Prefer costMetricsAllYears from simulations_list (localStorage)
    const baseSeries = this.getSeriesForRun(String(baseRun.id));
    const currSeries = this.getSeriesForRun(String(currentRun.id));

    const wantKeys = ['Diagnosed', 'Treated', 'Controlled', 'CVD_Patients', 'CVD_Events'] as const;

    if (baseSeries && currSeries) {
      const baseVals = this.readKpisFromSeries(baseSeries, yr, wantKeys);
      const currVals = this.readKpisFromSeries(currSeries, yr, wantKeys);

      const diagnosedPct = this.pct(currVals.Diagnosed, baseVals.Diagnosed);
      const treatedPct = this.pct(currVals.Treated, baseVals.Treated);
      const controlledPct = this.pct(currVals.Controlled, baseVals.Controlled);
      const cvdPatientsPct = this.pct(currVals.CVD_Patients, baseVals.CVD_Patients);
      const cvdEventsPct = this.pct(currVals.CVD_Events, baseVals.CVD_Events);

      this.kpiCards = [
        this.buildCard('Diagnosed', 'pi pi-user-plus', 'All patients diagnosed', diagnosedPct, currVals.Diagnosed),
        this.buildCard('Treated', 'pi pi-briefcase', 'Receiving treatment', treatedPct, currVals.Treated),
        this.buildCard('Controlled', 'pi pi-heart', 'Condition controlled', controlledPct, currVals.Controlled),
        this.buildCard('CVD patients', 'pi pi-users', 'Patients with CVD', cvdPatientsPct, currVals.CVD_Patients),
        this.buildCard('CVD events', 'pi pi-file', 'Recorded CVD events', cvdEventsPct, currVals.CVD_Events)
      ];
      return;
    }

    // Fallback: rows-based (existing behavior) if series missing or empty
    const yrNum = Number(yr);
    const baseRow = this.pickRowForYear(baseRun.rows, yrNum);
    const currRow = this.pickRowForYear(currentRun.rows, yrNum);

    if (!baseRow || !currRow) {
      this.kpiCards = this.zeroCards();
      return;
    }

    const diagnosedBase = this.readRowExact(baseRow, ['diagnosed_total', 'diagnosed']);
    const diagnosedCurr = this.readRowExact(currRow, ['diagnosed_total', 'diagnosed']);

    const treatedBase = this.readRowExact(baseRow, ['treated_total', 'treated']);
    const treatedCurr = this.readRowExact(currRow, ['treated_total', 'treated']);

    const controlledBase = this.readRowExact(baseRow, ['controlled_total', 'controlled'], v =>
      v ?? ((Number(baseRow?.controlled_male ?? 0) + Number(baseRow?.controlled_female ?? 0)) || 0)
    );
    const controlledCurr = this.readRowExact(currRow, ['controlled_total', 'controlled'], v =>
      v ?? ((Number(currRow?.controlled_male ?? 0) + Number(currRow?.controlled_female ?? 0)) || 0)
    );

    const cvdPatientsBase = this.readRowAny(baseRow, ['cvd_patients_total', 'CVD_patients_total', 'CVD_patients', 'cvd_patients']);
    const cvdPatientsCurr = this.readRowAny(currRow, ['cvd_patients_total', 'CVD_patients_total', 'CVD_patients', 'cvd_patients']);

    const cvdEventsBase = this.readRowAny(baseRow, ['cvd_events_total', 'CVD_Events_total', 'CVD_events_total', 'cvd_events', 'CVD_Events']);
    const cvdEventsCurr = this.readRowAny(currRow, ['cvd_events_total', 'CVD_Events_total', 'CVD_events_total', 'cvd_events', 'CVD_Events']);

    const diagnosedPct = this.toPct(diagnosedCurr, diagnosedBase);
    const treatedPct = this.toPct(treatedCurr, treatedBase);
    const controlledPct = this.toPct(controlledCurr, controlledBase);
    const cvdPatientsPct = this.toPct(cvdPatientsCurr, cvdPatientsBase);
    const cvdEventsPct = this.toPct(cvdEventsCurr, cvdEventsBase);

    this.kpiCards = [
      this.buildCard('Diagnosed', 'pi pi-user-plus', 'All patients diagnosed', diagnosedPct, diagnosedCurr),
      this.buildCard('Treated', 'pi pi-briefcase', 'Receiving treatment', treatedPct, treatedCurr),
      this.buildCard('Controlled', 'pi pi-heart', 'Condition controlled', controlledPct, controlledCurr),
      this.buildCard('CVD patients', 'pi pi-users', 'Patients with CVD', cvdPatientsPct, cvdPatientsCurr),
      this.buildCard('CVD events', 'pi pi-file', 'Recorded CVD events', cvdEventsPct, cvdEventsCurr)
    ];
  }

  private zeroCards(): KpiCard[] {
    return [
      this.buildCard('Diagnosed', 'pi pi-user-plus', 'All patients diagnosed', 0, 0),
      this.buildCard('Treated', 'pi pi-briefcase', 'Receiving treatment', 0, 0),
      this.buildCard('Controlled', 'pi pi-heart', 'Condition controlled', 0, 0),
      this.buildCard('CVD patients', 'pi pi-users', 'Patients with CVD', 0, 0),
      this.buildCard('CVD events', 'pi pi-file', 'Recorded CVD events', 0, 0)
    ];
  }

  private toPct(curr: number, base: number): number {
    if (!Number.isFinite(curr) || !Number.isFinite(base) || base === 0) return 0;
    return this.round1(((curr - base) / base) * 100);
  }

  private pickBaseRun(runs: any[]): any | null {
    if (!runs?.length) return null;
    const baseByTitle = runs.find(r => (r?.title || '').trim().toLowerCase() === 'basic simulation' || (r?.title || '').trim().toLowerCase() === 'simulation 1');
    return baseByTitle || runs[0] || null;
  }

  private pickCurrentRun(runs: any[], currentRunId: string | null): any | null {
    if (currentRunId) return runs.find(r => String(r.id) === String(currentRunId)) || null;
    // Fallback: pick the latest non-baseline run
    const nonBase = runs.filter(r => (r?.title || '').trim().toLowerCase() !== 'basic simulation' && (r?.title || '').trim().toLowerCase() !== 'simulation 1');
    return nonBase[nonBase.length - 1] || runs[runs.length - 1] || null;
  }

  private pickRowForYear(rows: any[], targetYear: number): any | null {
    if (!Array.isArray(rows) || !rows.length) return null;
    const exact = rows.find(r => Number(r?.year) === Number(targetYear));
    if (exact) return exact;
    const sorted = rows
      .filter(r => Number.isFinite(Number(r?.year)))
      .sort((a, b) => Number(a.year) - Number(b.year));
    const past = sorted.filter(r => Number(r.year) <= Number(targetYear)).pop();
    return past ?? sorted[0] ?? null;
  }

  private readRowExact(row: any, keys: string[], fallback?: (v: number | null | undefined) => number): number {
    if (!row) return 0;
    for (const k of keys) {
      const v = row[k];
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
    if (fallback) return fallback(null);
    return 0;
  }

  private readRowAny(row: any, keys: string[]): number {
    if (!row) return 0;
    for (const k of keys) {
      const n = this.extractNumeric(row[k]);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  }

  // ------------ Costs (unchanged) ------------
  private computeCosts(): void {
    const y = this.getResolvedYear();
    const effectiveCurrentCm = this.currentCm ?? this.baseCm;

    const primaryKey = `cumulative_primary_care_cost_current_${this.range}`;
    const secondaryKey = `cumulative_CVD_event_cost_current_${this.range}`;

    const basePrimary = this.readCmValue(this.baseCm, primaryKey, y);
    const currPrimary = this.readCmValue(effectiveCurrentCm, primaryKey, y);

    const baseSecondary = this.readCmValue(this.baseCm, secondaryKey, y);
    const currSecondary = this.readCmValue(effectiveCurrentCm, secondaryKey, y);

    this.primaryCost = { base: basePrimary, current: currPrimary, diff: Math.abs(currPrimary - basePrimary) };
    this.secondaryCost = { base: baseSecondary, current: currSecondary, diff: Math.abs(currSecondary - baseSecondary) };
  }

  private readCmValue(cm: any, key: string, year: string): number {
    if (!cm || !key) return 0;
    const obj = cm[key];
    const direct = obj?.[year] ?? obj?.[String(Number(year))];
    if (typeof direct === 'number' && !Number.isNaN(direct)) return direct;
    return this.parseNumber(direct);
  }

  // ------------ LocalStorage series helpers ------------
  private getSeriesForRun(runId: string): CmSeries | null {
    // Try localStorage first
    try {
      const raw = localStorage.getItem('simulations_list');
      if (raw) {
        const sims: any[] = JSON.parse(raw);
        const sim = Array.isArray(sims) ? sims.find(s => String(s.id) === String(runId)) : null;
        if (sim?.costMetricsAllYears?.series) return sim.costMetricsAllYears as CmSeries;
        // If found sim but no precomputed series, try to build it from stored rows inside simulations_list
        if (sim?.rows?.length) {
          return this.buildSeriesFromRunRows(sim);
        }
      }
    } catch { }

    // Fallback: try building from the run currently in AnalysisStore snapshot
    const run = this.runsSnapshot.find(r => String(r.id) === String(runId));
    if (run) {
      const built = this.buildSeriesFromRunRows(run);
      if (built) return built;
    }

    return null;
  }

  private readKpisFromSeries(
    cmSeries: CmSeries,
    year: string,
    keys: readonly ['Diagnosed', 'Treated', 'Controlled', 'CVD_Patients', 'CVD_Events']
  ): { Diagnosed: number; Treated: number; Controlled: number; CVD_Patients: number; CVD_Events: number } {
    const val = (k: string): number => {
      const byYear = cmSeries.series?.[k];
      const v = byYear ? byYear[year] ?? byYear[String(Number(year))] : undefined;
      const n = this.parseNumber(v);
      return Number.isFinite(n) ? n : 0;
    };
    return {
      Diagnosed: val(keys[0]),
      Treated: val(keys[1]),
      Controlled: val(keys[2]),
      CVD_Patients: val(keys[3]),
      CVD_Events: val(keys[4])
    };
  }

  // ------------ Utils ------------
  private parseNumber(v: any): number {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (typeof v === 'string') {
      const n = Number(v.replace(/,/g, ''));
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  }

  private extractNumeric(value: unknown): number {
    if (value == null) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.+-]/g, '');
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof value === 'boolean') return value ? 1 : 0;
    return 0;
  }

  private buildCard(label: string, icon: string, subtitle: string, percent: number, count: number): KpiCard {
    return { label, icon, subtitle, percent: this.round1(percent), count: this.round0(count) };
  }

  private round1(n: number): number {
    return Math.round((n + Number.EPSILON) * 10) / 10;
  }
  private round0(n: number): number {
    return Math.round(n ?? 0);
  }

  get resolvedYearForHeader(): string {
    return this.getResolvedYear();
  }

  public formatCost(value: number): string {
    const v = Number(value) || 0;
    const abs = Math.abs(v);
    if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(v));
  }

  // 2) Replace your buildSeriesFromRunRows with this version (uses strongly-typed keys → no TS4111)
  private buildSeriesFromRunRows(run: any): CmSeries | null {
    const rows: any[] = Array.isArray(run?.rows) ? run.rows : [];
    if (!rows.length) return null;

    const series: Record<KpiSeriesKey, Record<string, number>> = {
      Diagnosed: {},
      Treated: {},
      Controlled: {},
      CVD_Patients: {},
      CVD_Events: {}
    };

    for (const r of rows) {
      const y = String(r?.year ?? '');
      if (!/^\d{4}$/.test(y)) continue;

      // Diagnosed
      const diag = this.extractNumeric(r?.diagnosed_total ?? r?.diagnosed);
      if (Number.isFinite(diag)) series.Diagnosed[y] = diag;

      // Treated
      const tr = this.extractNumeric(r?.treated_total ?? r?.treated);
      if (Number.isFinite(tr)) series.Treated[y] = tr;

      // Controlled: prefer total, else male+female
      let ctrl = this.extractNumeric(r?.controlled_total ?? r?.controlled);
      if (!Number.isFinite(ctrl) || ctrl === 0) {
        const cm = this.extractNumeric(r?.controlled_male);
        const cf = this.extractNumeric(r?.controlled_female);
        const sum = cm + cf;
        if (Number.isFinite(sum) && sum > 0) ctrl = sum;
      }
      if (Number.isFinite(ctrl)) series.Controlled[y] = ctrl;

      // CVD patients
      const cvdp = this.extractNumeric(
        r?.cvd_patients_total ?? r?.CVD_patients_total ?? r?.CVD_Patients ?? r?.cvd_patients
      );
      if (Number.isFinite(cvdp)) series.CVD_Patients[y] = cvdp;

      // CVD events
      const cvde = this.extractNumeric(
        r?.cvd_events_total ?? r?.CVD_Events_total ?? r?.CVD_events_total ?? r?.cvd_events ?? r?.CVD_Events
      );
      if (Number.isFinite(cvde)) series.CVD_Events[y] = cvde;
    }

    const years = Array.from(
      new Set([
        ...Object.keys(series.Diagnosed),
        ...Object.keys(series.Treated),
        ...Object.keys(series.Controlled),
        ...Object.keys(series.CVD_Patients),
        ...Object.keys(series.CVD_Events)
      ])
    ).sort();

    return {
      discountRate: 0.03,
      discountSuffix: 'three',
      years,
      series
    } as CmSeries;
  }
  private pct(curr: number, base: number): number {
    if (!Number.isFinite(curr) || !Number.isFinite(base) || base === 0) return 0;
    return this.round1(((curr - base) / base) * 100);
  }
}